import { ImgID } from "../../../image_loader/img_id.js";
import { DialogManager } from "../../dialog/dialog_manager.js";
import { InviteFriendDialog } from "../../dialog/invite_friend_dialog.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { setButtonTextOutline } from "../../common_ui_elements/button_text_outline.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIAdaptiveElementsByTextHorisontal } from "../../common_ui_elements/ui_adaptive_elements_by_text_horisontal.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { CurrencyBar } from "../../common_ui_elements/currency_bar.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { purchaseItem } from "../../../offline/economy_gateway.js";
import { adoptEconomyFromPlayer } from "../../../api/player_sync.js";
import { logVirtualCurrencySpent } from "../../../analytics/appsflyer_events.js";
import { ApiError } from "../../../api/http_client.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { Easing } from "../../../transforms/transform_base.js";
import { OfferService } from "../../../offers/offer_service.js";
import { OfferTriggerController } from "../../../offers/offer_trigger_controller.js";
import type { OfferDto } from "../../../api/dto/offers.dto.js";
import { CONTINUE_SPECIAL_OFFER_PLACEMENT, ContinueSpecialOfferPanel } from "./continue_special_offer_panel.js";
import { Vector2 } from "../../../common/vector2.js";
import type { ReviveOfferDto } from "../../../api/dto/player.dto.js";
import { InviteFriendBlock } from "../../common_ui_elements/invite_friend_block.js";
import { InviteService } from "../../../friends/invite_service.js";

export interface ContinueOverlayParams
{
  offer: ReviveOfferDto;
  purchase_index: number;
  /** Level being played; reported with the continue_dialog_opened trigger event. */
  level?: number;
  onClose?: () => void;
  onBuy?: ( shots: number ) => void;
  /** Turns the dialog into the conveyor's "replay this level" offer; resolves true once bought. */
  conveyor_retry?: () => Promise<boolean>;
  /** Replaces the continue balls purchase with the caller's own one, keeping the dialog texts; resolves true once bought. */
  buy?: () => Promise<boolean>;
}

export class ContinueOverlay extends AbstractOverlay
{
  private static readonly DESIGN_W = 909;
  private static readonly BG_Y = 88;
  private static readonly BG_H = 1145;
  private static readonly DESIGN_H = ContinueOverlay.BG_Y + ContinueOverlay.BG_H;
  private static readonly INVITE_BOTTOM_MARGIN = 76;
  private static readonly CONTENT_ABOVE_CENTER = 120;
  private static readonly CONTENT_INVITE_GAP = 40;
  private static readonly CONTENT_MIN_Y = 190;

  private content_holder!: UIElementHolder;
  private purchase_inflight = false;
  private is_bought = false;
  private is_redirected_to_shop = false;
  private purchased_shots = 0;
  private coin_count_text: UITextElement | null = null;
  private shown_coins = -1;
  private next_coins_sync_at = 0;
  private special_offer_panel: ContinueSpecialOfferPanel | null = null;
  private content_shift_y = 0;
  private static active: ContinueOverlay | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: ContinueOverlayParams,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    this.buildContent();

    ContinueOverlay.active = this;

    if ( !this.isConveyorRetry() && !params.buy )
      this.requestSpecialOffer();
  }

  private buildContent(): void
  {
    this.createContentHolder();
    this.createBackground();
    this.createTitle();
    this.createDialogInfo();
    this.createCloseButton();
    this.createCoinBadge();
    this.createBuyButton();
    this.createInviteBlock();
  }

  private isConveyorRetry(): boolean
  {
    return ( this.params as ContinueOverlayParams ).conveyor_retry !== undefined;
  }

  private baseHolderY(): number
  {
    const holder_h = LayoutScaler.heightFromDesign( ContinueOverlay.DESIGN_H );
    const above_center = LayoutScaler.heightFromDesign( ContinueOverlay.CONTENT_ABOVE_CENTER );
    const invite_gap = LayoutScaler.heightFromDesign( ContinueOverlay.CONTENT_INVITE_GAP );
    const min_y = LayoutScaler.heightFromDesign( ContinueOverlay.CONTENT_MIN_Y );
    const preferred_y = this.rect.getH2() - holder_h / 2 - above_center;
    const max_y = this.inviteBlockTop() - invite_gap - holder_h;
    return Math.max( min_y, Math.min( preferred_y, max_y ) );
  }

  private inviteBlockTop(): number
  {
    const block_h = LayoutScaler.heightFromDesign( InviteFriendBlock.DESIGN_H );
    const bottom_margin = LayoutScaler.heightFromDesign( ContinueOverlay.INVITE_BOTTOM_MARGIN );
    return this.rect.getH() - block_h - bottom_margin;
  }

  private createInviteBlock(): void
  {
    const block_w = LayoutScaler.widthFromDesign( InviteFriendBlock.DESIGN_W );
    const block_x = this.rect.getW2() - block_w / 2;
    const block_y = this.inviteBlockTop();

    const block = new InviteFriendBlock( this.ctx, block_x, block_y, () => void InviteService.share() );
    this.addChild( block );
  }

  /**
   * The special-offer placement under the dialog comes either from the
   * catalog (no trigger rules) or from a trigger decision on
   * continue_dialog_opened; the latter arrives asynchronously through
   * presentSpecialOffer.
   */
  private requestSpecialOffer(): void
  {
    const resolved = OfferService.getPlacementOffer( CONTINUE_SPECIAL_OFFER_PLACEMENT );
    if ( resolved ) this.showSpecialOffer( resolved.offer );
    const level = ( this.params as ContinueOverlayParams ).level;
    void OfferTriggerController.report( 'continue_dialog_opened', level !== undefined ? { level } : {} );
  }

  /** Entry point for a trigger decision; false when no Continue dialog is up. */
  public static presentSpecialOffer( offer: OfferDto, trigger_event_id: string ): boolean
  {
    const overlay = ContinueOverlay.active;
    if ( !overlay || overlay.is_bought ) return false;
    overlay.showSpecialOffer( offer, trigger_event_id );
    return true;
  }

  private showSpecialOffer( offer: OfferDto, trigger_event_id?: string ): void
  {
    if ( this.special_offer_panel )
    {
      this.removeChild( this.special_offer_panel );
      this.special_offer_panel = null;
    }
    const panel_w = LayoutScaler.widthFromDesign( ContinueSpecialOfferPanel.DESIGN_W );
    const panel_h = LayoutScaler.heightFromDesign( ContinueSpecialOfferPanel.DESIGN_H );
    const gap = LayoutScaler.heightFromDesign( 24 );
    const x = this.rect.getW2() - panel_w / 2;
    const holder_h = LayoutScaler.heightFromDesign( ContinueOverlay.DESIGN_H );
    const centered_holder_y = this.baseHolderY();
    const content_bottom = this.inviteBlockTop();
    const overflow = centered_holder_y + holder_h + gap + panel_h + gap - content_bottom;
    const shift = Math.max( 0, Math.min( overflow, centered_holder_y - gap ) );
    if ( shift !== this.content_shift_y )
    {
      this.content_shift_y = shift;
      this.removeAllChildren();
      this.buildContent();
    }
    const dialog_bottom = centered_holder_y - shift + holder_h;
    const y = Math.min( dialog_bottom + gap, content_bottom - panel_h - gap );
    this.special_offer_panel = new ContinueSpecialOfferPanel( this.ctx, x, y, {
      offer,
      trigger_event_id,
      on_purchased: ( level_shots: number ) =>
      {
        if ( level_shots <= 0 )
        {
          // Inventory only: nothing to resume with, the player keeps deciding.
          this.syncCoins();
          return;
        }
        this.is_bought = true;
        this.purchased_shots = level_shots;
        OverlayManager.closeOverlay();
      },
    } );
    this.addChild( this.special_offer_panel );
    this.invalidateSubtree();
  }

  private createContentHolder(): void
  {
    const holder_w = LayoutScaler.widthFromDesign( ContinueOverlay.DESIGN_W );
    const holder_h = LayoutScaler.heightFromDesign( ContinueOverlay.DESIGN_H );
    const holder_x = this.rect.getW2() - holder_w / 2;
    const holder_y = this.baseHolderY() - this.content_shift_y;

    this.content_holder = new UIElementHolder( this.ctx, holder_x, holder_y, holder_w, holder_h );
    this.addChild( this.content_holder );
  }

  private createBackground(): void
  {
    const design_bg_x = 0;
    const design_bg_y = ContinueOverlay.BG_Y;
    const design_bg_h = ContinueOverlay.BG_H;

    const bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_bg_x ),
      LayoutScaler.yFromDesign( design_bg_y ),
      LayoutScaler.widthFromDesign( ContinueOverlay.DESIGN_W ),
      LayoutScaler.heightFromDesign( design_bg_h ),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.content_holder.addChild( bg );
  }

  private createTitle(): void
  {
    const design_title_x = 0;
    const design_title_y = ContinueOverlay.BG_Y + 68;
    const design_title_h = 110;
    const design_font_size = 75;
    const design_outline_w = 12;

    const title_full_w = LayoutScaler.widthFromDesign( ContinueOverlay.DESIGN_W );
    const MAGIC_NUM_CORRECTION = 0.8;
    const title_w = title_full_w * MAGIC_NUM_CORRECTION;

    const title = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_title_x ) + ( title_full_w - title_w ) / 2,
      LayoutScaler.yFromDesign( design_title_y ),
      title_w,
      LayoutScaler.heightFromDesign( design_title_h ),
      {
        localization_key: this.isConveyorRetry() ? "CONVEYOR.RETRY_TITLE" : "CONTINUE_DIALOG.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.settings_dialog_title_outline,
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    this.content_holder.addChild( title );
  }

  private createDialogInfo(): void
  {
    const shots = ( this.params as ContinueOverlayParams ).offer.shots;
    const design_card_w = 780;
    const design_card_h = 480;
    const design_card_x = ( ContinueOverlay.DESIGN_W - design_card_w ) / 2;
    const design_card_y = ContinueOverlay.BG_Y + 240;

    const card_w = LayoutScaler.widthFromDesign( design_card_w );
    const card_h = LayoutScaler.heightFromDesign( design_card_h );
    const card_x = LayoutScaler.xFromDesign( design_card_x );
    const card_y = LayoutScaler.yFromDesign( design_card_y );

    const card = new UIImage( this.ctx, card_x, card_y, card_w, card_h, ImgID.UI_PaternedBg );
    this.content_holder.addChild( card );

    if ( this.isConveyorRetry() )
    {
      this.createRetryText( card );
      return;
    }

    const design_bonus_y = 50;
    const design_bonus_h = 210;
    const design_bonus_font_size = 170;
    const design_bonus_outline_w = 20;

    const bonus_y = LayoutScaler.yFromDesign( design_bonus_y );
    const bonus_h = LayoutScaler.heightFromDesign( design_bonus_h );

    const bonus_text = new UITextElement(
      this.ctx,
      0,
      bonus_y,
      card_w,
      bonus_h,
      {
        text: `+${shots}`,
        font_size: LayoutScaler.textSizeFromDesign( design_bonus_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        outline_color: ColorScheme.continue_overlay_card_text,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_bonus_outline_w ),
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );
    card.addChild( bonus_text );

    const design_caption_y = 260;
    const design_caption_h = 160;
    const design_caption_font_size = 55;

    const MAGIC_NUM_CORRECTION = 0.9;
    const caption_w = card_w * MAGIC_NUM_CORRECTION;
    const caption_x = ( card_w - caption_w ) / 2;
    const caption_y = LayoutScaler.yFromDesign( design_caption_y );
    const caption_h = LayoutScaler.heightFromDesign( design_caption_h );

    const caption = new UITextElement(
      this.ctx,
      caption_x,
      caption_y,
      caption_w,
      caption_h,
      {
        localization_key: "CONTINUE_OVERLAY.DYNAMIC_TEXT",
        text_variable_params: [ String( shots ) ],
        font_size: LayoutScaler.textSizeFromDesign( design_caption_font_size ),
        font_weight: 900,
        text_color: ColorScheme.continue_overlay_card_text,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
      }
    );
    card.addChild( caption );
  }

  private createRetryText( card: UIImage ): void
  {
    const design_text_font_size = 62;
    const MAGIC_NUM_CORRECTION = 0.9;

    const card_w = card.getRect().getW();
    const card_h = card.getRect().getH();
    const text_w = card_w * MAGIC_NUM_CORRECTION;
    const text_x = ( card_w - text_w ) / 2;

    const text = new UITextElement(
      this.ctx,
      text_x,
      0,
      text_w,
      card_h,
      {
        localization_key: "CONVEYOR.RETRY_TEXT",
        font_size: LayoutScaler.textSizeFromDesign( design_text_font_size ),
        font_weight: 900,
        text_color: ColorScheme.continue_overlay_card_text,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
      }
    );
    card.addChild( text );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 114;
    const design_btn_x = ContinueOverlay.DESIGN_W - 80;
    const design_btn_y = ContinueOverlay.BG_Y - 55;

    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( design_btn_y ),
      LayoutScaler.widthFromDesign( design_btn_size ),
      LayoutScaler.heightFromDesign( design_btn_size ),
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );
    btn.setOnClick( () => OverlayManager.closeOverlay() );
    this.content_holder.addChild( btn );

    btn.setScale( new Vector2( 0, 0 ) );

    const btn_start   = performance.now();
    const btn_up_dur  = 176;
    const btn_set_dur = 112;

    btn.addTransform( new TransformScale(
      btn, true,
      new Vector2( 0, 0 ), new Vector2( 1.25, 1.25 ),
      btn_start, btn_up_dur,
      { easing: Easing.EaseOutQuad }
    ) );

    btn.addTransform( new TransformScale(
      btn, true,
      new Vector2( 1.25, 1.25 ), new Vector2( 1.0, 1.0 ),
      btn_start + btn_up_dur, btn_set_dur,
      { easing: Easing.EaseOutQuad }
    ) );
  }

  private createCoinBadge(): void
  {
    // Laid out exactly like the menu's coin block.
    const design_bg_w = 310;
    const design_bg_h = 100;
    const design_bg_x = 40;
    const design_bg_y = 86;

    const bg_w = LayoutScaler.widthFromDesign( design_bg_w );
    const bg_h = LayoutScaler.heightFromDesign( design_bg_h );
    const bg_x = LayoutScaler.xFromDesign( design_bg_x );
    const bg_y = LayoutScaler.yFromDesign( design_bg_y );

    const bg = new UIImage(
      this.ctx,
      bg_x,
      bg_y,
      bg_w,
      bg_h,
      ImgID.UI_MenuBlockBg
    );
    this.addChild( bg );

    const design_count_w = CurrencyBar.COUNT_W;
    const design_count_y = 0;
    const design_count_font_size = 45;

    const count_w = LayoutScaler.widthFromDesign( design_count_w );
    const count_h = bg_h;
    const count_right_pad = LayoutScaler.widthFromDesign( CurrencyBar.COUNT_RIGHT_PAD );
    const count_x = bg_w - count_w - count_right_pad;
    const count_y = -LayoutScaler.yFromDesign( design_count_y );

    const count_text = new UITextElement(
      this.ctx,
      count_x,
      count_y,
      count_w,
      count_h,
      {
        text: StatisticsStorage.load().coins.toString(),
        font_size: LayoutScaler.textSizeFromDesign( design_count_font_size ),
        font_weight: 700,
        text_color: ColorScheme.menu_top_el_text,
        text_style: TextStyleScheme.rubik,
      }
    );
    bg.addChild( count_text );
    this.coin_count_text = count_text;
    this.syncCoins();

    const design_icon_w = 156;
    const design_icon_h = 156;
    const design_icon_x = -35;

    const icon_w = LayoutScaler.widthFromDesign( design_icon_w );
    const icon_h = LayoutScaler.heightFromDesign( design_icon_h );
    const icon_x = LayoutScaler.xFromDesign( design_icon_x );
    const icon_y = bg_h / 2 - icon_h / 2;

    const icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_w,
      icon_h,
      ImgID.IconCoin
    );
    bg.addChild( icon );

    const design_plus_size = 65;
    const design_plus_margin_x = 10;
    const design_plus_margin_y = 10;

    const plus_w = LayoutScaler.widthFromDesign( design_plus_size );
    const plus_h = LayoutScaler.heightFromDesign( design_plus_size );
    const plus_x = icon_w - plus_w - LayoutScaler.xFromDesign( design_plus_margin_x );
    const plus_y = icon_h - plus_h - LayoutScaler.yFromDesign( design_plus_margin_y );

    const plus = new UIImage(
      this.ctx,
      plus_x,
      plus_y,
      plus_w,
      plus_h,
      ImgID.IconPlus,
      {},
      true
    );
    icon.addChild( plus );
  }

  private createBuyButton(): void
  {
    const price = ( this.params as ContinueOverlayParams ).offer.price;
    const design_btn_w = 700;
    const design_btn_h = 280;
    const design_btn_y = ContinueOverlay.DESIGN_H - design_btn_h - 55;
    const design_btn_x = ( ContinueOverlay.DESIGN_W - design_btn_w ) / 2;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );
    const btn_x = LayoutScaler.xFromDesign( design_btn_x );
    const btn_y = LayoutScaler.yFromDesign( design_btn_y );

    const btn = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      undefined,
      true
    );

    const design_text_font_size = 60;

    const text_options = {
      font_size: LayoutScaler.textSizeFromDesign( design_text_font_size ),
      font_weight: 800,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      outline_color: ColorScheme.menu_button_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      ...TextStyleScheme.buttonShadow(),
    };

    const buy_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        localization_key: "BUY",
        ...text_options,
      }
    );

    const design_icon_size = 104;
    const icon_size = LayoutScaler.widthFromDesign( design_icon_size );
    const icon = new UIImage(
      this.ctx, 0, 0, icon_size, icon_size, ImgID.IconCoin
    );

    const price_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        text: price.toString(),
        ...text_options,
      }
    );

    const design_content_gap = 0;

    const content = new UIAdaptiveElementsByTextHorisontal(
      this.ctx, 0, 0, btn_w, btn_h,
      [ buy_text, icon, price_text ],
      LayoutScaler.widthFromDesign( design_content_gap )
    );
    btn.addChild( content, true );

    const setActive = ( active: boolean ) =>
    {
      btn.setImageId( active ? ImgID.UI_GreenFrameBtn : ImgID.UI_GrayBtn );
      setButtonTextOutline( [ buy_text, price_text ], active, ColorScheme.menu_button_outline );
    };

    const can_afford = StatisticsStorage.load().coins >= price;

    if ( can_afford )
    {
      btn.setOnClick( () =>
      {
        if ( this.purchase_inflight )
          return;

        this.purchase_inflight = true;
        setActive( false );

        const custom_buy = ( this.params as ContinueOverlayParams ).conveyor_retry ?? ( this.params as ContinueOverlayParams ).buy;
        if ( custom_buy )
        {
          void custom_buy().then( bought =>
          {
            if ( bought )
            {
              this.is_bought = true;
              OverlayManager.closeOverlay();
              return;
            }
            this.purchase_inflight = false;
            setActive( true );
          } );
          return;
        }

        purchaseItem( "continue_balls", {
          purchase_index: ( this.params as ContinueOverlayParams ).purchase_index,
          price,
          qty: ( this.params as ContinueOverlayParams ).offer.shots,
        } )
          .then( r =>
          {
            adoptEconomyFromPlayer( r.player );
            void logVirtualCurrencySpent( {
              currency_name: 'coins',
              amount: r.purchased.price,
              sink: 'continue_balls',
            } );
            this.is_bought = true;
            this.purchased_shots = r.purchased.qty;
            OverlayManager.closeOverlay();
          } )
          .catch( e =>
          {
            console.warn( '[Economy] continue purchase failed', e );
            this.purchase_inflight = false;
            setActive( true );
            if ( e instanceof ApiError && e.status === 409 )
            {
              btn.setClickable( false );
              setActive( false );
            }
          } );
      } );
    }
    else
    {
      btn.setOnClick( () =>
      {
        DialogManager.openDialogAboveOverlays( new InviteFriendDialog() );
      } );
    }

    this.content_holder.addChild( btn );
  }

  public override onOverlayWillClose(): void
  {
    if ( ContinueOverlay.active === this ) ContinueOverlay.active = null;
    this.special_offer_panel?.trackClosed( this.is_bought ? 'revive_purchased' : this.is_redirected_to_shop ? 'redirected_to_shop' : 'dismissed' );
    if ( this.is_bought )
    {
      console.log('ContinueOverlay.onOverlayWillClose() is_bought');
      ( this.params as ContinueOverlayParams )?.onBuy?.( this.purchased_shots );
      return;
    }

    if ( this.is_redirected_to_shop )
      return;

    console.log('ContinueOverlay.onOverlayWillClose() is_redirected_to_shop');
    ( this.params as ContinueOverlayParams )?.onClose?.();
  }

  public name(): string
  {
    return "ContinueOverlay";
  }

  private syncCoins(): void
  {
    const coins = StatisticsStorage.load().coins;
    if ( coins === this.shown_coins )
      return;

    this.shown_coins = coins;
    this.coin_count_text?.setText( String( coins ) );
  }

  public override update( now: number ): void
  {
    super.update( now );

    if ( now >= this.next_coins_sync_at )
    {
      this.next_coins_sync_at = now + 250;
      this.syncCoins();
    }
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
